export interface MaterialField {
  value?: unknown;
  values?: unknown;
  unit?: unknown;
}

export interface DieMaterialItem {
  id?: string;
  category?: unknown;
  categoryName?: unknown;
  subCategory?: unknown;
  subcategory?: unknown;
  rowData?: Record<string, MaterialField | null | undefined> | null;
}

export interface DieSpecifications {
  name: string | null;
  sku: string | null;
  imageSrc: string | null;
  shape: string | null;
  unitSize: string | null;
  dieSize: string | null;
  dieUnitSize: string | null;
  finalUnitSize: string | null;
  columnsLines: string | null;
  spacing: string | null;
  type: string | null;
  subcategory: string | null;
  width: string | null;
  length: string | null;
  columns: string | null;
  lines: string | null;
  columnSpacing: string | null;
  lineSpacing: string | null;
  direction: string | null;
  price: string | null;
  active: boolean | null;
  outsourced: boolean | null;
}

const FIELD_KEYS = {
  sku: ["sku", "code"],
  name: ["name"],
  shape: ["shape"],
  image: ["image"],
  unitWidth: ["unitWidth", "unit width"],
  unitLength: ["unitLength", "unit length"],
  width: ["width"],
  length: ["length"],
  dieUnitWidth: ["dieUnitWidth"],
  dieUnitLength: ["dieUnitLength"],
  finalUnitWidth: ["finalUnitWidth"],
  finalUnitLength: ["finalUnitLength"],
  finalUnitHeight: ["finalUnitHeight"],
  columns: ["columns amount", "columnsAmount"],
  lines: ["lines amount", "linesAmount"],
  spaceBetweenColumns: ["spaceBetweenColumns", "space between columns"],
  spaceBetweenLines: ["spaceBetweenLines", "space between lines"],
  directions: ["directions", "direction"],
  price: ["price"],
  currency: ["currency"],
  active: ["Active", "active"],
  isOutSource: ["isOutSource", "isOutsource"],
  type: ["type"],
  subcategory: ["subcategory", "subCategory", "categoryName"],
};

export function isRawEnumKey(value: string): boolean {
  return /^[A-Za-z][A-Za-z0-9]*_[A-Za-z0-9_]+$/.test(value.trim());
}

export function humanizeEnumKey(value: string): string | null {
  const parts = value
    .trim()
    .split("_")
    .filter((part) => part.length > 0);
  if (parts.length < 2) {
    return null;
  }
  return parts
    .slice(1)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function formatScalar(value: unknown): string | null {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return null;
    }
    return String(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  return null;
}

function asMaterialField(raw: unknown): MaterialField | undefined {
  if (raw == null) {
    return undefined;
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as MaterialField;
  }
  return { value: raw };
}

export function getRowField(
  rowData: DieMaterialItem["rowData"],
  keys: string[]
): MaterialField | undefined {
  if (!rowData) {
    return undefined;
  }
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(rowData, key) && rowData[key] != null) {
      return asMaterialField(rowData[key]);
    }
  }
  const wanted = new Set(keys.map((key) => key.toLowerCase()));
  const match = Object.keys(rowData).find((key) => wanted.has(key.toLowerCase()));
  if (match && rowData[match] != null) {
    return asMaterialField(rowData[match]);
  }
  return undefined;
}

function unitOf(field: MaterialField | undefined): string | null {
  if (!field || field.unit === null || field.unit === undefined) {
    return null;
  }
  const unit = String(field.unit).trim();
  return unit ? unit : null;
}

function optionKey(entry: unknown): string | null {
  if (typeof entry === "string" || typeof entry === "number") {
    return String(entry);
  }
  if (entry && typeof entry === "object") {
    const record = entry as Record<string, unknown>;
    const key = record.key ?? record.id;
    if (key === null || key === undefined) {
      return null;
    }
    return String(key);
  }
  return null;
}

function optionLabel(entry: unknown): string | null {
  if (typeof entry === "string" || typeof entry === "number") {
    return formatScalar(entry);
  }
  if (entry && typeof entry === "object") {
    const record = entry as Record<string, unknown>;
    return formatScalar(record.value ?? record.label ?? record.name);
  }
  return null;
}

function lookupOptionLabel(values: unknown, raw: unknown): string | null {
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }
  const needle = String(raw).trim().toLowerCase();
  if (!needle) {
    return null;
  }
  const match = values.find((entry) => {
    const key = optionKey(entry);
    return key !== null && key.trim().toLowerCase() === needle;
  });
  if (!match) {
    return null;
  }
  const label = optionLabel(match);
  if (!label) {
    return null;
  }
  return isRawEnumKey(label) ? humanizeEnumKey(label) : label;
}

export function resolveFieldText(
  field: MaterialField | undefined,
  humanizeRawKeys = true
): string | null {
  if (!field) {
    return null;
  }
  const rawValues = Array.isArray(field.value) ? field.value : [field.value];
  const labels = rawValues
    .map((raw) => {
      if (raw === null || raw === undefined || raw === "") {
        return null;
      }
      const fromList = lookupOptionLabel(field.values, raw);
      if (fromList) {
        return fromList;
      }
      const scalar = formatScalar(raw);
      if (!scalar) {
        return null;
      }
      if (isRawEnumKey(scalar)) {
        return humanizeRawKeys ? humanizeEnumKey(scalar) : null;
      }
      return scalar;
    })
    .filter((label): label is string => Boolean(label));

  return labels.length > 0 ? labels.join(", ") : null;
}

export function readBoolean(field: MaterialField | undefined): boolean | null {
  if (!field) {
    return null;
  }
  const value = field.value;
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    return null;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }
  return null;
}

export function readImageSrc(field: MaterialField | undefined): string | null {
  if (typeof field?.value !== "string") {
    return null;
  }
  const src = field.value.trim();
  return src ? src : null;
}

export function formatMeasure(field: MaterialField | undefined): string | null {
  const text = formatScalar(field?.value);
  if (text === null) {
    return null;
  }
  const unit = unitOf(field);
  return unit ? `${text} ${unit}` : text;
}

export function formatPair(
  leftField: MaterialField | undefined,
  rightField: MaterialField | undefined
): string | null {
  const left = formatScalar(leftField?.value);
  const right = formatScalar(rightField?.value);
  if (left === null && right === null) {
    return null;
  }
  const leftUnit = unitOf(leftField);
  const rightUnit = unitOf(rightField);
  if (leftUnit && rightUnit && leftUnit !== rightUnit) {
    const leftText = left === null ? "—" : `${left} ${leftUnit}`;
    const rightText = right === null ? "—" : `${right} ${rightUnit}`;
    return `${leftText} × ${rightText}`;
  }
  const unit = leftUnit || rightUnit;
  const body = `${left ?? "—"} × ${right ?? "—"}`;
  return unit ? `${body} ${unit}` : body;
}

function formatTriple(
  fields: Array<MaterialField | undefined>
): string | null {
  const present = fields
    .map((field) => ({
      text: formatScalar(field?.value),
      unit: unitOf(field),
    }))
    .filter((entry): entry is { text: string; unit: string | null } => entry.text !== null);
  if (present.length === 0) {
    return null;
  }
  const uniqueUnits = Array.from(
    new Set(present.map((entry) => entry.unit).filter((unit): unit is string => Boolean(unit)))
  );
  const body = present.map((entry) => entry.text).join(" × ");
  return uniqueUnits.length === 1 ? `${body} ${uniqueUnits[0]}` : body;
}

function readPlain(field: MaterialField | undefined): string | null {
  if (!field) {
    return null;
  }
  if (Array.isArray(field.value)) {
    const parts = field.value
      .map((item) => formatScalar(item))
      .filter((item): item is string => Boolean(item));
    return parts.length > 0 ? parts.join(", ") : null;
  }
  return formatScalar(field.value);
}

function readSubcategory(item: DieMaterialItem, rowData: DieMaterialItem["rowData"]): string | null {
  const fromRow = readPlain(getRowField(rowData, FIELD_KEYS.subcategory));
  if (fromRow) {
    return fromRow;
  }
  const candidates = [item.subcategory, item.subCategory, item.categoryName, item.category];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
    if (candidate && typeof candidate === "object") {
      const record = candidate as Record<string, unknown>;
      const text = formatScalar(record.value ?? record.name ?? record.categoryName);
      if (text) {
        return text;
      }
    }
  }
  return null;
}

function formatPrice(
  priceField: MaterialField | undefined,
  currencyField: MaterialField | undefined
): string | null {
  if (!priceField) {
    return null;
  }
  const rawValues = Array.isArray(priceField.value) ? priceField.value : [priceField.value];
  const amounts = rawValues
    .map((value) => formatScalar(value))
    .filter((value): value is string => Boolean(value));
  if (amounts.length === 0) {
    return null;
  }
  const currency = resolveFieldText(currencyField, false);
  return currency ? `${amounts.join(" / ")} ${currency}` : amounts.join(" / ");
}

function omitDuplicate(value: string | null, existing: Array<string | null>): string | null {
  if (!value) {
    return null;
  }
  return existing.some((item) => item && item === value) ? null : value;
}

export function directionTranslationKey(label: string | null): "long" | "short" | null {
  if (!label) {
    return null;
  }
  const normalized = label.trim().toLowerCase();
  if (normalized === "long" || normalized.endsWith("_long")) {
    return "long";
  }
  if (normalized === "short" || normalized.endsWith("_short")) {
    return "short";
  }
  return null;
}

export function savedSelectionId(valueIds: unknown): string | null {
  if (valueIds == null || valueIds === "") {
    return null;
  }
  const id = Array.isArray(valueIds) ? valueIds[0] : valueIds;
  if (id == null || id === "") {
    return null;
  }
  return String(id);
}

export function findSavedGalleryItem<T extends { id?: string | number | null }>(
  items: T[] | null | undefined,
  valueIds: unknown
): T | null {
  const id = savedSelectionId(valueIds);
  if (!id || !Array.isArray(items)) {
    return null;
  }
  return items.find((item) => item?.id != null && String(item.id) === id) ?? null;
}

export function readDieSpecs(item: DieMaterialItem | null | undefined): DieSpecifications {
  const rowData = item?.rowData;
  const unitWidth = getRowField(rowData, FIELD_KEYS.unitWidth);
  const unitLength = getRowField(rowData, FIELD_KEYS.unitLength);
  const width = getRowField(rowData, FIELD_KEYS.width);
  const length = getRowField(rowData, FIELD_KEYS.length);
  const dieUnitWidth = getRowField(rowData, FIELD_KEYS.dieUnitWidth);
  const dieUnitLength = getRowField(rowData, FIELD_KEYS.dieUnitLength);
  const unitSize = formatPair(unitWidth, unitLength);
  const dieSize = omitDuplicate(formatPair(width, length), [unitSize]);
  const dieUnitSize = omitDuplicate(formatPair(dieUnitWidth, dieUnitLength), [unitSize, dieSize]);

  return {
    name: readPlain(getRowField(rowData, FIELD_KEYS.name)),
    sku: readPlain(getRowField(rowData, FIELD_KEYS.sku)),
    imageSrc: readImageSrc(getRowField(rowData, FIELD_KEYS.image)),
    shape: resolveFieldText(getRowField(rowData, FIELD_KEYS.shape)),
    unitSize,
    dieSize,
    dieUnitSize,
    finalUnitSize: formatTriple([
      getRowField(rowData, FIELD_KEYS.finalUnitWidth),
      getRowField(rowData, FIELD_KEYS.finalUnitLength),
      getRowField(rowData, FIELD_KEYS.finalUnitHeight),
    ]),
    columnsLines: formatPair(
      getRowField(rowData, FIELD_KEYS.columns),
      getRowField(rowData, FIELD_KEYS.lines)
    ),
    spacing: formatPair(
      getRowField(rowData, FIELD_KEYS.spaceBetweenColumns),
      getRowField(rowData, FIELD_KEYS.spaceBetweenLines)
    ),
    type: resolveFieldText(getRowField(rowData, FIELD_KEYS.type)),
    subcategory: item ? readSubcategory(item, rowData) : null,
    width: formatMeasure(unitWidth) ?? formatMeasure(width),
    length: formatMeasure(unitLength) ?? formatMeasure(length),
    columns: formatMeasure(getRowField(rowData, FIELD_KEYS.columns)),
    lines: formatMeasure(getRowField(rowData, FIELD_KEYS.lines)),
    columnSpacing: formatMeasure(getRowField(rowData, FIELD_KEYS.spaceBetweenColumns)),
    lineSpacing: formatMeasure(getRowField(rowData, FIELD_KEYS.spaceBetweenLines)),
    direction: resolveFieldText(getRowField(rowData, FIELD_KEYS.directions)),
    price: formatPrice(
      getRowField(rowData, FIELD_KEYS.price),
      getRowField(rowData, FIELD_KEYS.currency)
    ),
    active: readBoolean(getRowField(rowData, FIELD_KEYS.active)),
    outsourced: readBoolean(getRowField(rowData, FIELD_KEYS.isOutSource)),
  };
}
